import React from "react";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Link,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import { LoadingButton } from "@mui/lab";
import { MuiTelInput, MuiTelInputCountry } from "mui-tel-input";
import { useSite } from "hooks/use-site";
import { useAppSelector } from "store";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "store";
import { signUpWithReferral } from "service/api";
import { errorNotification } from "features/notifications/notificationsSlice";
import { setNewUser } from "features/register/registerSlice";

type InitialValues = {
  email: string;
  phone: string;
  password: string;
  passwordConfirmation: string;
  terms_agree_check: boolean;
  privacy_agree_check: boolean;
  age_agree_check: boolean;
  not_pep_check: boolean;
  not_us_resident_citizen_check: boolean;
};

const isILD = window.location.hostname.includes("iledgends");

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Email is invalid").required("Email is required"),
  phone: Yup.string()
    .required("Mobile is required")
    .test("is-valid-phone", "Invalid phone number", (value) => {
      if (!value) return false;
      const cleaned = value.replace(/\D/g, "");
      return cleaned.length >= 10 && cleaned.length <= 15;
    }),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[0-9]/, "Password must contain at least one number"),
  passwordConfirmation: Yup.string().oneOf(
    [Yup.ref("password"), null],
    "Passwords must match"
  ),
  terms_agree_check: Yup.boolean().oneOf([true], "You must agree to terms"),
  privacy_agree_check: Yup.boolean().oneOf(
    [true],
    "You must agree to privacy policy"
  ),
  age_agree_check: Yup.boolean().oneOf([true], "You must agree to age"),
  ...(isILD
    ? {}
    : {
        not_us_resident_citizen_check: Yup.boolean().oneOf(
          [true],
          "You must confirm that you are not a US resident/citizen"
        ),
        not_pep_check: Yup.boolean().oneOf(
          [true],
          "You must confirm that you are not a “politically exposed person” (PEP) nor an immediate family member of a PEP"
        ),
      }),
});

const SignUpWithPassword = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const newUser = useAppSelector((state) => state.register.newUser);

  const initialValues: InitialValues = {
    email: "",
    phone: "",
    password: "",
    passwordConfirmation: "",
    terms_agree_check: false,
    privacy_agree_check: false,
    age_agree_check: false,
    not_pep_check: false,
    not_us_resident_citizen_check: false,
  };

  if (!newUser) {
    navigate("/signin");
  } else {
    initialValues.email = newUser.email;
    initialValues.phone = newUser.phone;
  }

  const host = window.location.hostname;

  let defaultCountry: MuiTelInputCountry = "UA";

  if (host.indexOf("moonbitx") >= 0 || host.indexOf("kryptolad") >= 0) {
    defaultCountry = "PL";
    if (host.indexOf("ggd.") >= 0) {
      defaultCountry = "CA";
    }
  } else if (host.indexOf("bitchunters") >= 0) {
    defaultCountry = "IT";
  } else if (host.indexOf("coinspayworld") >= 0) {
    defaultCountry = "MX";
  }

  const {
    logo: logoSrc,
    termsAndConditionsUrl,
    privacyAndPolicyUrl,
    frontWalletAuthPageColor,
  } = useSite();

  const onSubmit = (
    { email, phone, password }: InitialValues,
    { setSubmitting }: FormikHelpers<InitialValues>
  ) => {
    if (!newUser) {
      return;
    }
    setSubmitting(true);
    const formattedPhone = phone.replace("+", "00").replace(" ", "").trim();
    const form3_json = localStorage.getItem("form3_json");
    signUpWithReferral({
      ...newUser,
      email,
      password,
      phone: formattedPhone,
      form3_json,
    })
      .then(() => {
        dispatch(setNewUser({ ...newUser, email, phone }));
        localStorage.removeItem("form3_json");
        navigate("/registration-completed");
      })
      .catch((err) => {
        dispatch(errorNotification(err?.message || "Something went wrong"));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const {
    handleSubmit,
    getFieldMeta,
    setFieldValue,
    handleChange,
    handleBlur,
    isValid,
    isSubmitting,
  } = useFormik<InitialValues>({
    initialValues,
    onSubmit,
    validationSchema,
  });

  const handlePhoneChange = (newValue: string) => {
    /*
                    matchIsValidTel(newValue, {
                      onlyCountryies: [], // optional,
                      excludedCountryies: [], // optional
                      continents: [] // optional
                    }) // true | false
                     */
    setFieldValue("phone", newValue);
  };

  return (
    <Box
      display={"flex"}
      alignItems="center"
      justifyContent="center"
      sx={{ mt: 4 }}
    >
      <Container maxWidth="md">
        <form onSubmit={handleSubmit}>
          <Card elevation={24} component={motion.div} layoutId="auth-card">
            <Box
              paddingY={2.5}
              paddingX={4}
              sx={{ bgcolor: `${frontWalletAuthPageColor}` }}
            >
              <img src={logoSrc} alt="logo" width={"200"} />
            </Box>
            <CardContent>
              <Stack spacing={4}>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <LockIcon fontSize="large" />
                  <Typography variant="h6" fontWeight={"600"}>
                    Sign Up
                  </Typography>
                </Box>
                <Grid container rowSpacing={2} columnSpacing={0.5}>
                  <Grid xs={12} item>
                    <TextField
                      label="Email"
                      name="email"
                      sx={{ flexShrink: 1 }}
                      variant="outlined"
                      value={getFieldMeta("email").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("email").touched &&
                        !!getFieldMeta("email").error
                      }
                      helperText={
                        getFieldMeta("email").touched &&
                        getFieldMeta("email").error
                      }
                    />
                  </Grid>

                  <Grid xs={12} item>
                    <TextField
                      label="Password"
                      name="password"
                      type="password"
                      sx={{ flexShrink: 1 }}
                      variant="outlined"
                      value={getFieldMeta("password").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("password").touched &&
                        !!getFieldMeta("password").error
                      }
                      helperText={
                        getFieldMeta("password").touched &&
                        getFieldMeta("password").error
                      }
                    />
                  </Grid>

                  <Grid xs={12} item>
                    <TextField
                      label="Retype Password"
                      name="passwordConfirmation"
                      type="password"
                      sx={{ flexShrink: 1 }}
                      variant="outlined"
                      value={getFieldMeta("passwordConfirmation").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("passwordConfirmation").touched &&
                        !!getFieldMeta("passwordConfirmation").error
                      }
                      helperText={
                        getFieldMeta("passwordConfirmation").touched &&
                        getFieldMeta("passwordConfirmation").error
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <MuiTelInput
                      excludedCountries={["IR"]}
                      focusOnSelectCountry
                      forceCallingCode
                      fullWidth
                      defaultCountry={defaultCountry}
                      label="Mobile"
                      name="phone"
                      value={getFieldMeta("phone").value}
                      onChange={handlePhoneChange}
                      error={
                        getFieldMeta("phone").touched &&
                        !!getFieldMeta("phone").error
                      }
                      helperText={
                        getFieldMeta("phone").touched &&
                        getFieldMeta("phone").error
                      }
                    />
                    <React.Fragment>
                      <Typography variant="body2" color="textSecondary">
                        Please select your country flag, then enter your phone
                        number without the country code.
                      </Typography>
                    </React.Fragment>
                  </Grid>
                </Grid>
                <Stack>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={
                        <Typography sx={{ display: "flex" }} component="span">
                          I agree to the{" "}
                          <Link
                            underline="hover"
                            sx={{ ml: 0.5 }}
                            href={termsAndConditionsUrl}
                            target="_blank"
                          >
                            terms and conditions
                          </Link>
                          <FormHelperText sx={{ color: "error.main", ml: 1 }}>
                            {getFieldMeta("terms_agree_check").touched &&
                              getFieldMeta("terms_agree_check").error &&
                              `(${getFieldMeta("terms_agree_check").error})`}
                          </FormHelperText>
                        </Typography>
                      }
                      name="terms_agree_check"
                      checked={getFieldMeta("terms_agree_check").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={
                        <Typography sx={{ display: "flex" }} component="span">
                          I agree to the{" "}
                          <Link
                            underline="hover"
                            sx={{ ml: 0.5 }}
                            href={privacyAndPolicyUrl}
                            target="_blank"
                          >
                            privacy policy
                          </Link>
                          <FormHelperText sx={{ color: "error.main", ml: 1 }}>
                            {getFieldMeta("privacy_agree_check").touched &&
                              getFieldMeta("privacy_agree_check").error &&
                              `(${getFieldMeta("privacy_agree_check").error})`}
                          </FormHelperText>
                        </Typography>
                      }
                      name="privacy_agree_check"
                      checked={getFieldMeta("privacy_agree_check").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={
                        <Typography sx={{ display: "flex" }} component="span">
                          I am over 18 years old and I have read and accepted
                          all the above.
                          <FormHelperText sx={{ color: "error.main", ml: 1 }}>
                            {getFieldMeta("age_agree_check").touched &&
                              getFieldMeta("age_agree_check").error &&
                              `(${getFieldMeta("age_agree_check").error})`}
                          </FormHelperText>
                        </Typography>
                      }
                      name="age_agree_check"
                      checked={getFieldMeta("age_agree_check").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormGroup>
                  {!isILD && (
                    <>
                      <FormGroup>
                        <FormControlLabel
                          control={<Checkbox />}
                          label={
                            <Typography
                              sx={{ display: "flex" }}
                              component="span"
                            >
                              I am not a US resident/citizen
                              <FormHelperText
                                sx={{ color: "error.main", ml: 1 }}
                              >
                                {getFieldMeta("not_us_resident_citizen_check")
                                  .touched &&
                                  getFieldMeta("not_us_resident_citizen_check")
                                    .error &&
                                  `(${
                                    getFieldMeta(
                                      "not_us_resident_citizen_check"
                                    ).error
                                  })`}
                              </FormHelperText>
                            </Typography>
                          }
                          name="not_us_resident_citizen_check"
                          checked={
                            getFieldMeta("not_us_resident_citizen_check").value
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </FormGroup>

                      <FormGroup>
                        <FormControlLabel
                          control={<Checkbox />}
                          label={
                            <Typography
                              sx={{ display: "flex" }}
                              component="span"
                            >
                              I am not a “politically exposed person” (PEP) nor
                              an immediate family member of a PEP
                              <FormHelperText
                                sx={{ color: "error.main", ml: 1 }}
                              >
                                {getFieldMeta("not_pep_check").touched &&
                                  getFieldMeta("not_pep_check").error &&
                                  `(${getFieldMeta("not_pep_check").error})`}
                              </FormHelperText>
                            </Typography>
                          }
                          name="not_pep_check"
                          checked={getFieldMeta("not_pep_check").value}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </FormGroup>
                    </>
                  )}
                  {!isILD && (
                    <>
                      <Typography variant="subtitle1">
                        <p>
                          * A Politically Exposed Person (PEP) is a person
                          entrusted with an influential and prominent political
                          position operating within a domestic or foreign
                          capacity.
                        </p>
                      </Typography>
                    </>
                  )}

                  <Typography variant="subtitle1">
                    <p>
                      * For Corporate account; the details must be of a director
                      or an authorized signatory.
                    </p>
                  </Typography>

                  <Typography variant="subtitle1">
                    <p>
                      * The Personal details must be real and according to your
                      ID/Passport/Driving licence.{" "}
                    </p>
                  </Typography>
                  <Typography variant="subtitle1">
                    <p>
                      * To verify information obtained from customers all names
                      are screened with the KYC tool and searched through
                      credible sources of commercially or publicly available
                      information.{" "}
                    </p>
                  </Typography>
                </Stack>
                <Stack spacing={2}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={2}
                    alignItems="center"
                  >
                    <LoadingButton
                      fullWidth
                      loading={isSubmitting}
                      variant="contained"
                      type="submit"
                      disabled={!isValid || isSubmitting}
                    >
                      Submit
                    </LoadingButton>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};
export default SignUpWithPassword;
